import { useCallback, useEffect, useState } from 'react';

/**
 * V4 Voice GM (2026-06-02) — narration verbosity mode.
 *
 * 'full'        — auto-narrate the complete paragraph text.
 * 'brief'       — auto-narrate only a short cue (paragraph number /
 *                 "continuing"). Manual ▶ Read Aloud still plays the
 *                 full text on demand.
 * 'first-visit' — V6.2.A (2026-06-02). Full narration on first visit;
 *                 brief cue on revisits. The visited set lives per-
 *                 campaign in localStorage (see useVisitedParagraphs).
 *
 * User pick (V4 Q3, 2026-06-02): default 'full', expose 'brief' as a
 * UI option in the Audio drawer. V6.2 added 'first-visit' as 3rd option.
 *
 * Persistence: localStorage 'briefing-room:narration-mode' with same
 * cross-instance CustomEvent sync pattern as useNarratorVoice / useMicActivation.
 */

export type NarrationMode = 'full' | 'brief' | 'first-visit';
const STORAGE_KEY = 'briefing-room:narration-mode';
export const NARRATION_MODE_DEFAULT: NarrationMode = 'full';

function isNarrationMode(v: unknown): v is NarrationMode {
  return v === 'full' || v === 'brief' || v === 'first-visit';
}

function readStored(): NarrationMode {
  if (typeof window === 'undefined') {
    return NARRATION_MODE_DEFAULT;
  }
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (isNarrationMode(v)) {
      return v;
    }
  } catch {
    // degrade quietly
  }
  return NARRATION_MODE_DEFAULT;
}

function writeStored(value: NarrationMode): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // degrade quietly
  }
}

export function useNarrationMode(): [NarrationMode, (next: NarrationMode) => void] {
  const [mode, setModeState] = useState<NarrationMode>(readStored);

  useEffect(() => {
    const handler = (e: Event) => {
      if (e instanceof CustomEvent && isNarrationMode(e.detail)) {
        setModeState(e.detail);
        return;
      }
      setModeState(readStored());
    };
    window.addEventListener('briefing-room:narration-mode-changed', handler);
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('briefing-room:narration-mode-changed', handler);
      window.removeEventListener('storage', handler);
    };
  }, []);

  const setMode = useCallback((next: NarrationMode) => {
    setModeState(next);
    writeStored(next);
    window.dispatchEvent(
      new CustomEvent('briefing-room:narration-mode-changed', { detail: next }),
    );
  }, []);

  return [mode, setMode];
}
